import React from "react";
import { motion } from "framer-motion";
import {
  Home,
  Utensils,
  Car,
  Baby,
  HeartHandshake,
  Stethoscope,
} from "lucide-react";

const services = [
  { name: "Maid", icon: Home, popular: true, tone: "indigo" },
  { name: "Cook", icon: Utensils, popular: true, tone: "orange" },
  { name: "Driver", icon: Car, tone: "sky" },
  { name: "Nanny", icon: Baby, tone: "pink" },
  { name: "Caretaker", icon: HeartHandshake, tone: "emerald" },
  { name: "Nurse", icon: Stethoscope, tone: "violet" },
];

const toneStyles = {
  indigo: {
    iconWrap:
      "from-indigo-50 to-violet-50 border-indigo-100 text-indigo-700",
    badge: "bg-indigo-50 text-indigo-700 border-indigo-100",
    glow: "from-indigo-100/50 via-transparent to-violet-100/35",
  },
  orange: {
    iconWrap:
      "from-orange-50 to-amber-50 border-orange-100 text-orange-700",
    badge: "bg-orange-50 text-orange-700 border-orange-100",
    glow: "from-orange-100/50 via-transparent to-amber-100/35",
  },
  sky: {
    iconWrap: "from-sky-50 to-cyan-50 border-sky-100 text-sky-700",
    badge: "bg-sky-50 text-sky-700 border-sky-100",
    glow: "from-sky-100/50 via-transparent to-cyan-100/35",
  },
  pink: {
    iconWrap: "from-pink-50 to-rose-50 border-pink-100 text-pink-700",
    badge: "bg-pink-50 text-pink-700 border-pink-100",
    glow: "from-pink-100/50 via-transparent to-rose-100/35",
  },
  emerald: {
    iconWrap:
      "from-emerald-50 to-teal-50 border-emerald-100 text-emerald-700",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-100",
    glow: "from-emerald-100/50 via-transparent to-teal-100/35",
  },
  violet: {
    iconWrap:
      "from-violet-50 to-purple-50 border-violet-100 text-violet-700",
    badge: "bg-violet-50 text-violet-700 border-violet-100",
    glow: "from-violet-100/50 via-transparent to-purple-100/35",
  },
};

const ServiceGrid = () => {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#fbfaff_0%,#f4f0ff_42%,#fdfbff_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(99,102,241,0.10),transparent_22%),radial-gradient(circle_at_86%_20%,rgba(168,85,247,0.08),transparent_20%),radial-gradient(circle_at_82%_82%,rgba(56,189,248,0.06),transparent_22%)]" />

      {/* Large soft art */}
      <div className="absolute -top-24 -left-16 w-72 h-72 bg-indigo-200/25 rounded-full blur-3xl" />
      <div className="absolute top-[12%] right-[-80px] w-80 h-80 bg-purple-200/20 rounded-full blur-3xl" />
      <div className="absolute bottom-[-100px] left-[12%] w-64 h-64 bg-sky-200/20 rounded-full blur-3xl" />

      {/* Subtle line texture */}
      <div className="absolute inset-0 opacity-[0.035] [background-image:linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] [background-size:36px_36px]" />

      {/* Top wave */}
      <svg
        className="absolute top-0 left-0 w-full h-[90px] md:h-[130px] opacity-60"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="rgba(255,255,255,0.6)"
          d="M0,128L60,117.3C120,107,240,85,360,85.3C480,85,600,107,720,122.7C840,139,960,149,1080,133.3C1200,117,1320,75,1380,53.3L1440,32L1440,0L0,0Z"
        />
      </svg>

      <div className="relative max-w-6xl mx-auto px-4">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-indigo-700 font-medium mb-5 shadow-sm">
            Trusted Support for Every Home
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Our Services
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Explore the kinds of home support families most commonly choose through A2Z.
          </p>
        </motion.div>

        {/* Mobile-first clean grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {services.map((service, i) => {
            const Icon = service.icon;
            const tone = toneStyles[service.tone];

            return (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="group relative overflow-hidden rounded-[28px] border border-white/80 bg-white/78 backdrop-blur-xl p-5 md:p-6 shadow-[0_14px_35px_rgba(15,23,42,0.06)] hover:shadow-[0_18px_42px_rgba(15,23,42,0.10)] transition-all duration-300 min-h-[180px] flex flex-col justify-between"
              >
                {/* Hover wash */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-br ${tone.glow} pointer-events-none`}
                />

                {/* top */}
                <div className="relative flex items-start justify-between gap-3 mb-5">
                  <div
                    className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl border bg-gradient-to-br ${tone.iconWrap} flex items-center justify-center shadow-sm shrink-0`}
                  >
                    <Icon className="w-7 h-7" />
                  </div>

                  {service.popular ? (
                    <span
                      className={`inline-flex px-3 py-1.5 rounded-full text-xs font-medium border ${tone.badge}`}
                    >
                      Popular
                    </span>
                  ) : null}
                </div>

                {/* middle */}
                <div className="relative">
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
                    {service.name}
                  </h3>
                  <p className="text-sm md:text-[15px] text-gray-600 leading-relaxed">
                    Trusted support designed for daily home needs.
                  </p>
                </div>

                {/* bottom accent only, no button */}
                <div className="relative mt-5">
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-3" />
                  <p className="text-sm font-medium text-indigo-600">
                    Explore in hire section
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServiceGrid;